import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import MenuAdmin from '../composants/MenuAdmin';
import { formaterPrix } from '../services/format';

function lireFichierEnDataUrl(fichier) {
  return new Promise((resolve, reject) => {
    if (!fichier) return reject(new Error('Fichier requis'));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.readAsDataURL(fichier);
  });
}

function Modale({ ouverte, titre, onFermer, children }) {
  if (!ouverte) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onMouseDown={onFermer}>
      <div
        className="w-full max-w-2xl rounded-3xl border border-gray-200 bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
          <div className="text-sm font-semibold text-gray-900">{titre}</div>
          <button
            onClick={onFermer}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export default function DashboardAdmin() {
  const [onglet, setOnglet] = useState('produits');
  const [modale, setModale] = useState(null);

  const [categories, setCategories] = useState([]);
  const [produits, setProduits] = useState([]);
  const [publicites, setPublicites] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sponsorises, setSponsorises] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [utilisateurs, setUtilisateurs] = useState([]);

  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');

  const [formCategorie, setFormCategorie] = useState({ nom: '', description: '' });
  const [formProduit, setFormProduit] = useState({ nom: '', description: '', categorie_id: '', actif: true });
  const [formImage, setFormImage] = useState({ produit_id: '', fichier: null, ordre_affichage: 0 });
  const [formVariante, setFormVariante] = useState({ produit_id: '', nom_variante: '', valeur_variante: '', prix: 0, stock: 0, reference_sku: '' });

  const [formPub, setFormPub] = useState({ titre: '', description: '', fichier: null, lien: '', position: 0, actif: true });
  const [formVideo, setFormVideo] = useState({ titre: '', description: '', url_video: '', miniature: '', position: 0, actif: true });
  const [formSpons, setFormSpons] = useState({ produit_id: '', position: 0, date_debut: '', date_fin: '' });

  const produitsParId = useMemo(() => new Map(produits.map((p) => [p.id, p])), [produits]);

  async function chargerTout() {
    const [cats, prods, pubs, vids, spons, comms, users] = await Promise.all([
      api.get('/categories'),
      api.get('/produits?actif=true'),
      api.get('/publicites'),
      api.get('/videos'),
      api.get('/produits/sponsorises/admin'),
      api.get('/commandes'),
      api.get('/utilisateurs')
    ]);

    setCategories(cats.data);
    setProduits(prods.data);
    setPublicites(pubs.data);
    setVideos(vids.data);
    setSponsorises(spons.data);
    setCommandes(comms.data);
    setUtilisateurs(users.data);
  }

  useEffect(() => {
    let mounted = true;

    async function run() {
      setErreur('');
      try {
        await chargerTout();
      } catch (e) {
        if (!mounted) return;
        setErreur(e?.response?.data?.message || 'Chargement admin impossible');
      }
    }

    run();
    return () => {
      mounted = false;
    };
  }, []);

  async function action(fn) {
    setErreur('');
    setMessage('');
    try {
      await fn();
      await chargerTout();
      setMessage('Enregistré');
      return true;
    } catch (e) {
      setErreur(e?.response?.data?.message || e?.message || 'Action impossible');
      return false;
    }
  }

  return (
    <div className="grid gap-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-gray-600">Administration</div>
        <div className="mt-2 text-xl font-bold text-gray-900">Dashboard</div>
      </div>

      {erreur ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{erreur}</div>
      ) : null}
      {message ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">{message}</div>
      ) : null}

      <MenuAdmin actif={onglet} onChange={setOnglet} />

      {onglet === 'categories' ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setModale('categorie')}
              className="rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
            >
              Nouvelle catégorie
            </button>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Nom</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {categories.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{c.id}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{c.nom}</td>
                      <td className="px-4 py-3 text-gray-700">{c.description || ''}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => action(async () => api.delete(`/categories/${c.id}`))}
                          className="rounded-xl px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Modale ouverte={modale === 'categorie'} titre="Ajouter une catégorie" onFermer={() => setModale(null)}>
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={formCategorie.nom}
                  onChange={(e) => setFormCategorie((s) => ({ ...s, nom: e.target.value }))}
                  placeholder="Nom"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formCategorie.description}
                  onChange={(e) => setFormCategorie((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Description"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
              </div>
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    await api.post('/categories', formCategorie);
                    setFormCategorie({ nom: '', description: '' });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>
        </div>
      ) : null}

      {onglet === 'produits' ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setModale('produit')}
              className="rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
            >
              Nouveau produit
            </button>
            <button
              onClick={() => setModale('image')}
              className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
            >
              Ajouter une image
            </button>
            <button
              onClick={() => setModale('variante')}
              className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50"
            >
              Ajouter une variante
            </button>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Produit</th>
                    <th className="px-4 py-3">Catégorie</th>
                    <th className="px-4 py-3">Images</th>
                    <th className="px-4 py-3">Variantes</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {produits.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{p.id}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{p.nom}</div>
                        <div className="mt-0.5 text-xs text-gray-500 line-clamp-1">{p.description || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.categorie?.nom || ''}</td>
                      <td className="px-4 py-3 text-gray-700">{(p.images || []).length}</td>
                      <td className="px-4 py-3 text-gray-700">{(p.variantes || []).length}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => action(async () => api.delete(`/produits/${p.id}`))}
                          className="rounded-xl px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                        >
                          Désactiver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Modale
            ouverte={modale === 'produit'}
            titre="Ajouter un produit"
            onFermer={() => setModale(null)}
          >
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={formProduit.nom}
                  onChange={(e) => setFormProduit((s) => ({ ...s, nom: e.target.value }))}
                  placeholder="Nom"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <select
                  value={formProduit.categorie_id}
                  onChange={(e) => setFormProduit((s) => ({ ...s, categorie_id: e.target.value }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                >
                  <option value="">Catégorie</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nom}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                value={formProduit.description}
                onChange={(e) => setFormProduit((s) => ({ ...s, description: e.target.value }))}
                placeholder="Description"
                className="min-h-24 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
              />
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    await api.post('/produits', {
                      ...formProduit,
                      categorie_id: Number(formProduit.categorie_id)
                    });
                    setFormProduit({ nom: '', description: '', categorie_id: '', actif: true });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>

          <Modale
            ouverte={modale === 'image'}
            titre="Ajouter une image produit"
            onFermer={() => setModale(null)}
          >
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-3">
                <select
                  value={formImage.produit_id}
                  onChange={(e) => setFormImage((s) => ({ ...s, produit_id: e.target.value }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                >
                  <option value="">Produit</option>
                  {produits.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormImage((s) => ({ ...s, fichier: e.target.files?.[0] || null }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formImage.ordre_affichage}
                  onChange={(e) => setFormImage((s) => ({ ...s, ordre_affichage: e.target.value }))}
                  placeholder="Ordre"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
              </div>
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    if (!formImage.produit_id) throw new Error('Produit requis');
                    if (!formImage.fichier) throw new Error('Image requise');
                    const url_image = await lireFichierEnDataUrl(formImage.fichier);
                    await api.post(`/produits/${Number(formImage.produit_id)}/images`, {
                      url_image,
                      ordre_affichage: Number(formImage.ordre_affichage)
                    });
                    setFormImage({ produit_id: '', fichier: null, ordre_affichage: 0 });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>

          <Modale
            ouverte={modale === 'variante'}
            titre="Ajouter une variante"
            onFermer={() => setModale(null)}
          >
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <select
                  value={formVariante.produit_id}
                  onChange={(e) => setFormVariante((s) => ({ ...s, produit_id: e.target.value }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                >
                  <option value="">Produit</option>
                  {produits.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
                <input
                  value={formVariante.nom_variante}
                  onChange={(e) => setFormVariante((s) => ({ ...s, nom_variante: e.target.value }))}
                  placeholder="Nom variante (Taille/Couleur/Volume...)"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVariante.valeur_variante}
                  onChange={(e) => setFormVariante((s) => ({ ...s, valeur_variante: e.target.value }))}
                  placeholder="Valeur (S/M/L...)"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVariante.reference_sku}
                  onChange={(e) => setFormVariante((s) => ({ ...s, reference_sku: e.target.value }))}
                  placeholder="SKU"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVariante.prix}
                  onChange={(e) => setFormVariante((s) => ({ ...s, prix: e.target.value }))}
                  placeholder="Prix"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVariante.stock}
                  onChange={(e) => setFormVariante((s) => ({ ...s, stock: e.target.value }))}
                  placeholder="Stock"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
              </div>
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    await api.post(`/produits/${Number(formVariante.produit_id)}/variantes`, {
                      nom_variante: formVariante.nom_variante,
                      valeur_variante: formVariante.valeur_variante,
                      prix: Number(formVariante.prix),
                      stock: Number(formVariante.stock),
                      reference_sku: formVariante.reference_sku || null
                    });
                    setFormVariante({ produit_id: '', nom_variante: '', valeur_variante: '', prix: 0, stock: 0, reference_sku: '' });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>
        </div>
      ) : null}

      {onglet === 'publicites' ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setModale('publicite')}
              className="rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
            >
              Nouvelle publicité
            </button>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Titre</th>
                    <th className="px-4 py-3">Position</th>
                    <th className="px-4 py-3">Lien</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {publicites.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{p.id}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{p.titre}</td>
                      <td className="px-4 py-3 text-gray-700">{p.position}</td>
                      <td className="px-4 py-3 text-gray-700">{p.lien || ''}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => action(async () => api.delete(`/publicites/${p.id}`))}
                          className="rounded-xl px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Modale ouverte={modale === 'publicite'} titre="Ajouter une publicité" onFermer={() => setModale(null)}>
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={formPub.titre}
                  onChange={(e) => setFormPub((s) => ({ ...s, titre: e.target.value }))}
                  placeholder="Titre"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formPub.lien}
                  onChange={(e) => setFormPub((s) => ({ ...s, lien: e.target.value }))}
                  placeholder="Lien"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormPub((s) => ({ ...s, fichier: e.target.files?.[0] || null }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formPub.position}
                  onChange={(e) => setFormPub((s) => ({ ...s, position: e.target.value }))}
                  placeholder="Position"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
              </div>
              <textarea
                value={formPub.description}
                onChange={(e) => setFormPub((s) => ({ ...s, description: e.target.value }))}
                placeholder="Description"
                className="min-h-24 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
              />
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    const image_url = formPub.fichier ? await lireFichierEnDataUrl(formPub.fichier) : null;
                    await api.post('/publicites', {
                      titre: formPub.titre,
                      description: formPub.description,
                      image_url,
                      lien: formPub.lien,
                      position: Number(formPub.position)
                    });
                    setFormPub({ titre: '', description: '', fichier: null, lien: '', position: 0, actif: true });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>
        </div>
      ) : null}

      {onglet === 'videos' ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setModale('video')}
              className="rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
            >
              Nouvelle vidéo
            </button>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Titre</th>
                    <th className="px-4 py-3">Position</th>
                    <th className="px-4 py-3">URL</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {videos.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{v.id}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{v.titre}</td>
                      <td className="px-4 py-3 text-gray-700">{v.position}</td>
                      <td className="px-4 py-3 text-gray-700">{v.url_video || ''}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => action(async () => api.delete(`/videos/${v.id}`))}
                          className="rounded-xl px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Modale ouverte={modale === 'video'} titre="Ajouter une vidéo" onFermer={() => setModale(null)}>
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={formVideo.titre}
                  onChange={(e) => setFormVideo((s) => ({ ...s, titre: e.target.value }))}
                  placeholder="Titre"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVideo.url_video}
                  onChange={(e) => setFormVideo((s) => ({ ...s, url_video: e.target.value }))}
                  placeholder="URL vidéo (embed)"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVideo.miniature}
                  onChange={(e) => setFormVideo((s) => ({ ...s, miniature: e.target.value }))}
                  placeholder="Miniature"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  value={formVideo.position}
                  onChange={(e) => setFormVideo((s) => ({ ...s, position: e.target.value }))}
                  placeholder="Position"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
              </div>
              <textarea
                value={formVideo.description}
                onChange={(e) => setFormVideo((s) => ({ ...s, description: e.target.value }))}
                placeholder="Description"
                className="min-h-24 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
              />
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    await api.post('/videos', {
                      ...formVideo,
                      position: Number(formVideo.position)
                    });
                    setFormVideo({ titre: '', description: '', url_video: '', miniature: '', position: 0, actif: true });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>
        </div>
      ) : null}

      {onglet === 'sponsorises' ? (
        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setModale('sponsorise')}
              className="rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
            >
              Nouveau sponsorisé
            </button>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Produit</th>
                    <th className="px-4 py-3">Position</th>
                    <th className="px-4 py-3">Début</th>
                    <th className="px-4 py-3">Fin</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sponsorises.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{s.id}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">
                        {s.produit?.nom || produitsParId.get(s.produit_id)?.nom || 'Produit'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{s.position}</td>
                      <td className="px-4 py-3 text-gray-700">{s.date_debut ? String(s.date_debut).slice(0, 10) : ''}</td>
                      <td className="px-4 py-3 text-gray-700">{s.date_fin ? String(s.date_fin).slice(0, 10) : ''}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => action(async () => api.delete(`/produits/sponsorises/admin/${s.id}`))}
                          className="rounded-xl px-3 py-2 text-sm text-rose-700 hover:bg-rose-50"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Modale ouverte={modale === 'sponsorise'} titre="Ajouter un produit sponsorisé" onFermer={() => setModale(null)}>
            <div className="grid gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <select
                  value={formSpons.produit_id}
                  onChange={(e) => setFormSpons((s) => ({ ...s, produit_id: e.target.value }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                >
                  <option value="">Produit</option>
                  {produits.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
                <input
                  value={formSpons.position}
                  onChange={(e) => setFormSpons((s) => ({ ...s, position: e.target.value }))}
                  placeholder="Position"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  type="date"
                  value={formSpons.date_debut}
                  onChange={(e) => setFormSpons((s) => ({ ...s, date_debut: e.target.value }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
                <input
                  type="date"
                  value={formSpons.date_fin}
                  onChange={(e) => setFormSpons((s) => ({ ...s, date_fin: e.target.value }))}
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none"
                />
              </div>
              <button
                onClick={async () => {
                  const ok = await action(async () => {
                    await api.post('/produits/sponsorises/admin', {
                      ...formSpons,
                      produit_id: Number(formSpons.produit_id),
                      position: Number(formSpons.position),
                      date_debut: formSpons.date_debut || null,
                      date_fin: formSpons.date_fin || null
                    });
                    setFormSpons({ produit_id: '', position: 0, date_debut: '', date_fin: '' });
                  });
                  if (ok) setModale(null);
                }}
                className="w-fit rounded-2xl bg-marega-600 px-5 py-3 text-sm font-semibold text-white hover:bg-marega-500"
              >
                Ajouter
              </button>
            </div>
          </Modale>
        </div>
      ) : null}

      {onglet === 'commandes' ? (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Utilisateur</th>
                  <th className="px-4 py-3">Montant</th>
                  <th className="px-4 py-3">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {commandes.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{c.id}</td>
                    <td className="px-4 py-3 text-gray-700">#{c.utilisateur_id}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{formaterPrix(c.montant_total)}</td>
                    <td className="px-4 py-3 text-gray-700">{c.statut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {onglet === 'utilisateurs' ? (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Rôle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {utilisateurs.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{u.id}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{u.nom}</td>
                    <td className="px-4 py-3 text-gray-700">{u.email}</td>
                    <td className="px-4 py-3 text-gray-700">{u.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {onglet === 'produits' || onglet === 'categories' || onglet === 'publicites' || onglet === 'videos' || onglet === 'sponsorises' || onglet === 'commandes' || onglet === 'utilisateurs' ? null : (
        <div className="rounded-3xl border border-gray-200 bg-white p-8 text-sm text-gray-600">Sélectionnez un onglet.</div>
      )}
    </div>
  );
}
